import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

import { User } from '@core/models/user';
import { UserService } from '@core/services/user.service';
import { AuthTokenService } from '@core/services/auth-token.service';

interface UserState {
  user: User | null;
  loading: boolean;
  error: HttpErrorResponse | null;
}

const initialState: UserState = { user: null, loading: false, error: null };

export const UserStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ user }) => ({
    mustSignServiceProviderContract: computed(() => {
      return (user()?.serviceProvider?.yearsSinceContractSigned ?? 0) >= 1;
    }),
    // Decisión de producto 2026-08-03 (post-migración): operar IO COINS se
    // gatea SOLO por KYC Validado (3), como en el sistema antiguo. Los estados
    // del DNI (frontal/reverso) son informativos para la revisión del admin y
    // NO bloquean — 17 de los 55 migrados operaban hace años con DNI sin
    // aprobar por-lado y la regla estricta de IN-470 QA #30 los dejó fuera.
    iocoinsUnlocked: computed(() => {
      return user()?.profile?.kycStatus === 3;
    }),
    spSeverity: computed(() => {
      const status = user()?.profile?.kycStatus;
      if (status === undefined) return 'secondary';
      if (status === 3) return 'success';
      if ([0, 1, 6].includes(status)) return 'warn';
      if ([2, 4, 5].includes(status)) return 'danger';
      return 'secondary';
    }),
    spLabel: computed(() => {
      const status = user()?.profile?.kycStatus;
      if (status === undefined) return 'Sin activar';
      if (status === 3) return 'Aprobado';
      if ([0, 1, 6].includes(status)) return 'Pendiente';
      if ([2, 4, 5].includes(status)) return 'Rechazado';
      return 'Sin activar';
    }),
  })),
  withMethods(
    (store, userService = inject(UserService), authTokenService = inject(AuthTokenService)) => {
      const refreshUser = () => {
        const hasActiveSession =
          Boolean(authTokenService.getAuthToken()) && !authTokenService.isTokenExpired();

        if (!hasActiveSession) {
          patchState(store, initialState);
          return EMPTY;
        }

        patchState(store, { loading: true, error: null });
        return userService.me().pipe(
          tap({
            next: (user: User) => {
              patchState(store, { user, loading: false });
            },
            error: (error: HttpErrorResponse) => {
              patchState(store, { error, loading: false });
            },
          })
        );
      };

      return {
        reset(): void {
          patchState(store, initialState);
        },
        refreshUser,
        loadUser: rxMethod<void>(pipe(switchMap(() => refreshUser()))),
      };
    }
  ),
  withHooks({
    onInit: store => store.loadUser(),
  })
);
